import React, { useState, useMemo } from "react";
import { Container, Row, Col, ProgressBar, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import BackArrow from "../../../../components/backArrow";
import "./viewmarketerprofile.css";
import Marscriptionmdl from "../../Subs_Modal";
import { getAsset } from "../../../../helpers/helper";
import { useViewMarketerProfile } from "../../../../hooks/useViewMarketerProfile";
import {
  useGetSubscriptionPurchaseByShopQuery,
  useGetHireRequestStatusQuery,
} from "../../../../features/marketing/marketingApiSlice";
import MassengerModals from "../../../../components/Navbar/MassengerModal";
import dummyUser from "../../../../assets/images/dummy/user-avatar-img.jpg";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { decodeId } from "../../../../utils/idObfuscator";

/* ─── Hire Button Helper */
const HireButton = ({ hireStatus, statusLoading, onClick }) => {
  if (statusLoading) {
    return (
      <Button variant="secondary" size="sm" disabled>
        <span
          className="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        />
        Checking...
      </Button>
    );
  }

  if (
    hireStatus === "completed" ||
    hireStatus === "approved" ||
    hireStatus === "success"
  ) {
    return (
      <Button
        variant="success"
        disabled
        size="sm"
        className="ps-4 pe-4"
        style={{ cursor: "default", opacity: 1 }}
      >
        Hired
      </Button>
    );
  }

  if (hireStatus === "pending") {
    return (
      <Button
        variant="warning"
        disabled
        style={{ cursor: "default", opacity: 1 }}
      >
        Pending Approval
      </Button>
    );
  }

  return (
    <Button variant="primary" onClick={onClick}>
      Hire Me
    </Button>
  );
};

const sameId = (a, b) => {
  const left = a != null ? String(a).trim() : "";
  const right = b != null ? String(b).trim() : "";
  return Boolean(left && right && left === right);
};

const industryLabel = (list) => {
  if (!Array.isArray(list) || !list.length) return "";
  return list
    .map((item) =>
      typeof item === "string" ? item : item?.name || item?.title || "",
    )
    .filter(Boolean)
    .join(" | ");
};

/* ─── Page ── */
const ViewMarketerProfile = () => {
  const { id: rawId } = useParams();

  const isEncrypted = rawId && rawId.startsWith("pvid=");
  const marketerId = isEncrypted ? decodeId(rawId) : rawId;
  const isValidMarketerId =
    marketerId &&
    (marketerId.startsWith("MA-") || /^[0-9a-fA-F]{24}$/.test(marketerId));
  const validationError = !isValidMarketerId
    ? { message: "Invalid or tampered profile ID. Access is restricted." }
    : null;

  const [showAllReviews, setShowAllReviews] = useState(false);
  const [lgShow, setLgShow] = useState(false);
  const [lgMsgShow, setLgMsgShow] = useState(false);

  const {
    profileData,
    topReview,
    reviews,
    projectDetail,
    loading,
    error: apiError,
    timeAgo,
  } = useViewMarketerProfile(marketerId);

  const error = apiError || validationError;

  const { name: loggedInUser } = useSelector((state) => state.user) || {};
  const loggedInUserId = loggedInUser?._id;
  const isOwnProfile =
    !!loggedInUserId &&
    (profileData?.userId?._id === loggedInUserId ||
      profileData?.userId === loggedInUserId);

  // Hire status is ALWAYS scoped to the shop currently hiring from —
  // another shop's pending/approved hire must not affect this shop.
  const shopId =
    localStorage.getItem("shopid") ||
    localStorage.getItem("OpenShopIdForMarketingByMarketer");

  const agentDocId = profileData?._id;
  const agentPublicId = profileData?.marketerId;

  const isThisShopHiringThisMarketer = (hiredMarketer) => {
    if (!hiredMarketer || hiredMarketer === "N/A") return false;
    const hired = String(
      hiredMarketer?._id || hiredMarketer?.id || hiredMarketer,
    );
    return (
      sameId(hired, agentDocId) ||
      sameId(hired, agentPublicId) ||
      sameId(hired, marketerId)
    );
  };

  const { data: shopPurchaseResponse, isLoading: purchaseLoading } =
    useGetSubscriptionPurchaseByShopQuery(shopId, {
      skip: !shopId || !profileData,
    });

  const { data: hireRequestResponse, isLoading: hireLoading } =
    useGetHireRequestStatusQuery(
      { shopId, marketerId: agentDocId },
      { skip: !shopId || !agentDocId },
    );

  const statusLoading = purchaseLoading || hireLoading;

  const { hireStatus, isThisMarketerHired } = useMemo(() => {
    const hireReq = hireRequestResponse?.data;
    const shopPurchase = shopPurchaseResponse?.data;

    // Prefer explicit hire-request for this shop + marketer
    if (hireReq && isThisShopHiringThisMarketer(hireReq.marketerId)) {
      const st = String(hireReq.status || "").toLowerCase();
      if (st === "rejected") {
        return { hireStatus: null, isThisMarketerHired: false };
      }
      return {
        hireStatus: st || "pending",
        isThisMarketerHired: true,
      };
    }

    // Fallback: purchasedMarketing for THIS shop only
    if (
      shopPurchase &&
      isThisShopHiringThisMarketer(shopPurchase.hiredMarketer)
    ) {
      const pay = String(shopPurchase.paymentStatus || "").toLowerCase();
      return {
        hireStatus: pay || "pending",
        isThisMarketerHired: true,
      };
    }

    return { hireStatus: null, isThisMarketerHired: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hireRequestResponse,
    shopPurchaseResponse,
    agentDocId,
    agentPublicId,
    marketerId,
  ]);

  const initialUserForChat = profileData?.userId
    ? {
        _id: profileData.userId._id,
        fullname: profileData.userId.fullname,
        image: profileData.userId.imageUrl?.[0] || null,
      }
    : null;

  // ── Loading skeleton ──
  if (loading) {
    return (
      <Container>
        <Row className="justify-content-center">
          <Col lg="8" className="mx-auto bg-light rounded p-3">
            <div className="profile-page min-vh-100 py-2 screenHeight">
              <div className="text-center mb-5">
                <div className="profile-avatar mx-auto mb-3">
                  <Skeleton circle width={120} height={120} />
                </div>
                <h3 className="profile-name mb-1">
                  <Skeleton width={180} />
                </h3>
                <p className="text-muted small mb-1">
                  <Skeleton width={120} />
                </p>
                <p className="text-muted small">
                  <Skeleton width={260} />
                </p>
              </div>

              <Row className="g-4 mb-5">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Col md={4} key={index}>
                    <div className="stat-card text-center">
                      <div className="stat-label">
                        <Skeleton width={100} />
                      </div>
                      <div className="stat-value">
                        <Skeleton width={50} />
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              <div className="feedback-section p-4 bg-white rounded-4 shadow-sm">
                <h4 className="mb-4 fw-bold">
                  <Skeleton width={200} />
                </h4>
                <div className="reviews-list">
                  {Array.from({ length: 2 }).map((_, index) => (
                    <div
                      key={index}
                      className="review-item p-4 bg-light rounded-3 mb-3"
                    >
                      <div className="d-flex align-items-start">
                        <Skeleton circle width={60} height={60} />
                        <div className="flex-grow-1 ms-3">
                          <Skeleton width={140} height={20} />
                          <Skeleton width={180} height={16} className="mt-2" />
                          <Skeleton count={2} className="mt-3" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // ── Error state ──
  if (error) {
    const errorMsg = error?.data?.message || error?.message || "Access Denied";
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div
          className="text-center p-5 bg-white rounded-4 shadow-sm border border-light"
          style={{ maxWidth: "450px" }}
        >
          <div className="text-danger mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              fill="currentColor"
              className="bi bi-shield-lock-fill"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M8 0c-.69 0-1.843.265-2.928.56-1.11.3-2.229.655-2.887.87a1.54 1.54 0 0 0-1.044 1.262c-.596 4.477.787 7.795 2.465 9.99a11.8 11.8 0 0 0 2.517 2.453c.386.273.744.482 1.048.625.28.132.581.24.829.24s.548-.108.829-.24a7 7 0 0 0 1.048-.625 11.8 11.8 0 0 0 2.517-2.453c1.678-2.195 3.061-5.513 2.465-9.99a1.54 1.54 0 0 0-1.044-1.263 62 62 0 0 0-2.887-.87C9.843.266 8.69 0 8 0m0 5a1.5 1.5 0 0 1 .5 2.915V9a1.5 1.5 0 0 1-3 0V7.915A1.5 1.5 0 0 1 8 5"
              />
            </svg>
          </div>
          <h4 className="fw-bold mb-3">Access Restricted</h4>
          <p className="text-muted mb-4">{errorMsg}</p>
          <Button variant="primary" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      </Container>
    );
  }

  if (!profileData) {
    return (
      <div className="text-center p-5 text-danger">No marketer data found</div>
    );
  }

  return (
    <Container className="pb-5 mb-5">
      {/* Subscription plan modal */}
      <Marscriptionmdl
        lgShow={lgShow}
        setLgShow={setLgShow}
        marketerId={profileData?._id}
      />

      {/* Messenger modal */}
      <MassengerModals
        lgMsgShow={lgMsgShow}
        setLgMsgShow={setLgMsgShow}
        initialUser={initialUserForChat}
        onInitialUserConsumed={() => {}}
      />

      <Row>
        <Col lg="8" className="mx-auto bg-light rounded p-3 mb-5 pb-5">
          <div className="profile-page min-vh-100 pt-2 pb-5 mb-5 screenHeight">
            {/* Back Arrow + Hire Button */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <BackArrow />
              {(!isOwnProfile || isThisMarketerHired) && (
                <div className="d-flex gap-2">
                  {(hireStatus === "completed" ||
                    hireStatus === "approved" ||
                    hireStatus === "success") && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setLgMsgShow(true)}
                    >
                      Message
                    </Button>
                  )}
                  <HireButton
                    hireStatus={hireStatus}
                    statusLoading={statusLoading}
                    onClick={() => setLgShow(true)}
                  />
                </div>
              )}
            </div>

            {/* ================= PROFILE HEADER ================= */}
            <div className="text-center mb-5">
              <div className="profile-avatar mx-auto mb-3">
                <img
                  src={
                    profileData?.userId?.imageUrl?.[0]
                      ? /^https?:\/\//i.test(
                          String(profileData.userId.imageUrl[0]),
                        )
                        ? profileData.userId.imageUrl[0]
                        : getAsset(profileData.userId.imageUrl[0])
                      : dummyUser
                  }
                  alt={profileData?.userId?.fullname || "Marketer profile"}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = dummyUser;
                  }}
                />
              </div>

              <h3 className="profile-name mb-1">
                {profileData?.userId?.fullname}
              </h3>

              <p className="text-muted small mb-1">
                {profileData?.MCity && `Zone: ${profileData.MCity}`}
              </p>

              <p className="text-muted small">
                Marketing expert for{" "}
                <span className="text-primary fw-semibold">
                  {[
                    industryLabel(profileData?.MIndustryName),
                    industryLabel(profileData?.MCategoryName),
                  ]
                    .filter(Boolean)
                    .join(" | ") || "—"}
                </span>
              </p>
            </div>

            {/* ================= STATS CARDS ================= */}
            <Row className="g-4 mb-5">
              <Col md={4}>
                <div className="stat-card text-center">
                  <div className="stat-label">Completed projects</div>
                  <div className="stat-value">
                    {projectDetail?.completedProjects ?? "N/A"}
                  </div>
                </div>
              </Col>

              <Col md={4}>
                <div className="stat-card text-center">
                  <div className="stat-label">Ongoing Projects</div>
                  <div className="stat-value">
                    {projectDetail?.ongoingProjects ?? "N/A"}
                  </div>
                </div>
              </Col>

              <Col md={4}>
                <div className="stat-card text-center">
                  <div className="stat-label">Total Clients</div>
                  <div className="stat-value">
                    {projectDetail?.totalClients ?? "N/A"}
                  </div>
                </div>
              </Col>
            </Row>

            {/* ================= RATINGS SECTION ================= */}
            <div className="feedback-section p-4 bg-white rounded-4 shadow-sm">
              <h4 className="mb-4 fw-bold">Ratings &amp; Feedback</h4>

              {topReview ? (
                <Row className="align-items-center mb-4">
                  <Col md={4} className="text-center text-md-start">
                    <div className="display-4 fw-bold">
                      {typeof topReview?.averageRating === "number"
                        ? topReview.averageRating.toFixed(1)
                        : (topReview?.averageRating ?? "N/A")}
                    </div>
                    <div className="stars mb-1">
                      {"★".repeat(topReview?.averageRating || 0)}
                    </div>
                    <div className="text-muted small">
                      {topReview?.totalReviews || 0} reviews
                    </div>
                  </Col>

                  <Col md={8}>
                    <div className="rating-bars">
                      {[5, 4, 3, 2, 1].map((star) => {
                        const total = topReview?.totalReviews || 1;
                        const value =
                          star === 5
                            ? topReview?.ratingRatio?.fiveStar
                            : star === 4
                              ? topReview?.ratingRatio?.fourStar
                              : star === 3
                                ? topReview?.ratingRatio?.threeStar
                                : star === 2
                                  ? topReview?.ratingRatio?.twoStar
                                  : topReview?.ratingRatio?.oneStar;
                        const percent = (value / total) * 100 || 0;

                        return (
                          <div
                            key={star}
                            className="rating-row d-flex align-items-center mb-2"
                          >
                            <span className="star-label me-2">{star}</span>
                            <div className="progress-flex">
                              <ProgressBar
                                now={percent}
                                className="flex-grow-1 me-3"
                              />
                            </div>
                            <span className="percentage">
                              {percent.toFixed(0)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className="text-center p-4 text-muted">
                  <p>No ratings yet</p>
                </div>
              )}

              {/* ================= REVIEWS ================= */}
              <div className="reviews-list">
                {reviews && reviews.length > 0 ? (
                  <>
                    {(showAllReviews ? reviews : reviews.slice(0, 1)).map(
                      (review) => (
                        <div
                          key={review._id}
                          className="review-item p-4 bg-light rounded-3 mb-3"
                        >
                          <div className="d-flex align-items-start">
                            <img
                              src={
                                review?.shopid?.image?.[0]
                                  ? getAsset(review.shopid.image[0])
                                  : dummyUser
                              }
                              alt={review?.shopid?.shopname || "reviewer"}
                              className="reviewer-avatar me-3"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.currentTarget.removeAttribute("crossorigin");
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = dummyUser;
                              }}
                            />
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-0">
                                    {review?.shopid?.shopname}
                                  </h6>
                                  <small className="text-muted">
                                    {timeAgo(review.createdAt)}
                                  </small>
                                </div>
                                <div className="stars">
                                  {"★".repeat(review.rating)}
                                  {"☆".repeat(5 - review.rating)}
                                </div>
                              </div>
                              <p className="mt-2 mb-3">{review.comment}</p>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </>
                ) : (
                  <div className="text-center p-4 text-muted">
                    <p>No reviews yet</p>
                  </div>
                )}
              </div>

              {!showAllReviews && reviews && reviews.length > 1 && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline-primary"
                    className="view-more-btn"
                    onClick={() => setShowAllReviews(true)}
                  >
                    View More
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ViewMarketerProfile;
